import { useState } from 'react'
import clsx from 'clsx'
import Linked from 'shared-components/service/Linked'
import { safeImageUrl, threadHref } from 'lib/community/model'
import {
  LEDGER_FOOTNOTE,
  bestDayNote,
  breakdownRows,
  dayBars,
  dayChartLabel,
  dayLabel,
  daysSum,
  fmtCount,
  fmtSeconds,
  followersLine,
  gearRows,
  heroSentence,
  ledgerRows,
  monthBars,
  monthChartLabel,
  monthLabel,
  monthNote,
  monthSoFarLine,
  photoNote,
  pointsCard,
  possessive,
  rangeFor,
  rankHint,
  readingCompare,
  readingNote,
  readingTimeState,
  reviewRows,
  sliceRange,
  todaySoFarLine,
  type Bar,
  type RangeId,
  type Voice,
} from 'lib/community/stats'
import type { OwnerCommunityMemberStats } from 'lib/dealhunt/types'
import styles from './member-stats.module.css'

/**
 * The Stats tab's cards (Member Stats design 1b–1d, 1f–1h). Pure: every figure comes from the
 * public stats payload and is drawn for everyone; `voice.own` (true only after `/me` says the page
 * is the reader's, and never under "View as visitor") switches the person and adds the coaching.
 */

type Stats = OwnerCommunityMemberStats

/** A card's eyebrow title. */
const Eyebrow: React.FC<React.PropsWithChildren<{ as?: 'h2' | 'h3' }>> = ({
  as: Tag = 'h2',
  children,
}) => <Tag className={styles.eyebrow}>{children}</Tag>

// ── bars ──────────────────────────────────────────────────────────────────────

/** One bar chart: plain divs, one hue, the figure on the tallest bar only. `role="img"` with the
 *  values as a sentence; the per-month chart also has a hidden table (`MonthTable`). */
const BarChart: React.VFC<{
  barsShown: readonly Bar[]
  kind: 'month' | 'day'
  label: string
}> = ({ barsShown, kind, label }) => (
  <div
    className={clsx(styles.plot, kind === 'day' && styles.plotDays)}
    style={kind === 'month' ? { gridTemplateColumns: monthColumns(barsShown.length) } : undefined}
    role="img"
    aria-label={label}
  >
    {barsShown.map(b => (
      <div key={b.key} className={styles.col}>
        {b.showLabel && (
          <span className={clsx(styles.barLabel, styles[`align_${b.align}`])}>{b.label}</span>
        )}
        {b.isNull ? (
          <span className={styles.striped} />
        ) : (
          <span
            className={clsx(
              kind === 'day' ? styles.dayBar : styles.bar,
              b.tone === 'cur' && styles.barCur,
              b.tone === 'zero' && styles.barZero
            )}
            style={{ height: `${b.height}px` }}
          />
        )}
      </div>
    ))}
  </div>
)

/** Up to 6 months: fixed 56 px columns (design 1b); more: the full width (1d). */
function monthColumns(n: number): string {
  return n > 6 ? `repeat(${n}, minmax(0, 1fr))` : `repeat(${n}, 56px)`
}

/** The per-month figures as a table for screen readers — the chart's text alternative (the
 *  ledger folds quiet months, so it cannot be). */
const MonthTable: React.VFC<{ months: Stats['readers']['months'] }> = ({ months }) => (
  <table className={styles.srOnly}>
    <caption>{'Readers and time spent reading, per month'}</caption>
    <thead>
      <tr>
        <th scope="col">{'Month'}</th>
        <th scope="col">{'Readers'}</th>
        <th scope="col">{'Average time spent reading'}</th>
      </tr>
    </thead>
    <tbody>
      {months.map(m => (
        <tr key={m.month}>
          <th scope="row">{monthLabel(m.month, 'longYear')}</th>
          <td>{typeof m.readers === 'number' ? fmtCount(m.readers) : 'not recorded'}</td>
          <td>{fmtSeconds(m.read_seconds_avg) ?? '—'}</td>
        </tr>
      ))}
    </tbody>
  </table>
)

// ── Readers ───────────────────────────────────────────────────────────────────

export const ReadersCard: React.VFC<{ stats: Stats; voice: Voice }> = ({ stats, voice }) => {
  const range = rangeFor(stats.since, stats.month)
  const [chosen, setChosen] = useState<RangeId>(range.initial)
  const shownRange: RangeId = range.pills ? chosen : 'all'
  const shown = monthBars(stats, shownRange)
  const days = dayBars(stats)
  const note = monthNote(stats, shown, voice)
  const firstDay = stats.readers.days[0]?.day
  return (
    <section className={clsx(styles.card, styles.cardWide)} aria-labelledby="stats-readers">
      <div className={styles.cardHead}>
        <h2 className={styles.eyebrow} id="stats-readers">
          {'Readers'}
        </h2>
        {range.pills ? (
          <div className={styles.pills} role="group" aria-label="Range">
            {range.options.map(o => (
              <button
                key={o.id}
                type="button"
                className={clsx(styles.pill, o.id === chosen && styles.pillOn)}
                aria-pressed={o.id === chosen}
                onClick={() => setChosen(o.id)}
              >
                {o.label}
              </button>
            ))}
          </div>
        ) : (
          <span className={styles.since}>{range.sinceLabel}</span>
        )}
      </div>
      <div className={styles.figures}>
        <div className={styles.figure}>
          <span className={styles.hero}>{fmtCount(stats.readers.all_time)}</span>
          <span className={styles.figureLabel}>{'readers all time'}</span>
        </div>
        <div className={styles.figure}>
          <span className={clsx(styles.big, styles.blue)}>
            {fmtCount(stats.readers.month_so_far)}
          </span>
          <span className={styles.figureLabel}>{`${monthLabel(stats.month, 'long')} so far`}</span>
        </div>
        <div className={styles.figure}>
          <span className={styles.big}>{fmtCount(stats.readers.people_90d)}</span>
          <span className={styles.figureLabel}>{'different people, last 90 days'}</span>
        </div>
      </div>
      <p className={styles.sentence}>{heroSentence(stats, voice)}</p>
      <div className={styles.charts}>
        <div className={styles.chart}>
          <div className={styles.chartHead}>
            <h3 className={styles.chartTitle}>{'Per month'}</h3>
            <span className={styles.muted}>{monthSoFarLine(stats)}</span>
          </div>
          <BarChart barsShown={shown} kind="month" label={monthChartLabel(shown)} />
          <div
            className={styles.axis}
            style={{ gridTemplateColumns: monthColumns(shown.length) }}
            aria-hidden="true"
          >
            {shown.map(b => (
              <span key={b.key}>{b.axis}</span>
            ))}
          </div>
          {note && <p className={styles.note}>{note}</p>}
          <MonthTable months={sliceRange(stats.readers.months, shownRange)} />
        </div>
        {days.length > 0 && (
          <div className={styles.chart}>
            <div className={styles.chartHead}>
              <h3 className={styles.chartTitle}>{'Last 14 days'}</h3>
              <span className={styles.muted}>{daysSum(stats)}</span>
            </div>
            <BarChart barsShown={days} kind="day" label={dayChartLabel(days)} />
            <div className={styles.dayAxis} aria-hidden="true">
              <span>{dayLabel(firstDay)}</span>
              <span>{'Today'}</span>
            </div>
            <p className={styles.note}>{`${bestDayNote(stats)} · ${todaySoFarLine(stats)}`}</p>
          </div>
        )}
      </div>
    </section>
  )
}

// ── Time spent reading ───────────────────────────────────────────────────────

export const ReadingTimeCard: React.VFC<{ stats: Stats; voice: Voice }> = ({ stats, voice }) => {
  const state = readingTimeState(stats.reading_time, stats.community.median_read_seconds, voice)
  if (state.kind !== 'figure') {
    // B.1: no average yet — "Collecting" since a day, or "starts counting soon"; no bars drawn.
    return (
      <section className={styles.card}>
        <Eyebrow>{'Time spent reading'}</Eyebrow>
        {state.kind === 'collecting' && (
          <div className={styles.figure}>
            <span className={styles.big}>{state.figure}</span>
            <span className={styles.figureLabel}>{'average per reader, last 90 days'}</span>
          </div>
        )}
        <p className={styles.note}>{state.line}</p>
      </section>
    )
  }
  const rows = readingCompare(stats, voice)
  return (
    <section className={styles.card}>
      <Eyebrow>{'Time spent reading'}</Eyebrow>
      <div className={styles.figure}>
        <span className={styles.big}>{state.figure}</span>
        <span className={styles.figureLabel}>{'average per reader, last 90 days'}</span>
      </div>
      {rows.length > 0 && (
        <div className={styles.rows}>
          {rows.map(r => (
            <div key={r.tone} className={styles.compareRow}>
              <span className={styles.rowLabel}>{r.label}</span>
              <span className={styles.track} aria-hidden="true">
                <span
                  className={clsx(styles.fill, r.tone === 'community' && styles.fillLight)}
                  style={{ width: `${r.pct}%` }}
                />
              </span>
              <span className={styles.rowValue}>{r.value}</span>
            </div>
          ))}
        </div>
      )}
      <p className={styles.note}>{readingNote(stats, voice)}</p>
    </section>
  )
}

// ── Readers by owner review ──────────────────────────────────────────────────

export const ReadersByReviewCard: React.VFC<{
  stats: Stats
  voice: Voice
  trailingSlash: boolean
  /** The summary's photo bonus (`points.photo_verified`); the note is the member's own. */
  photoPoints: number | null
}> = ({ stats, voice, trailingSlash, photoPoints }) => {
  const rows = reviewRows(stats)
  const note = voice.own ? photoNote(stats, photoPoints) : null
  return (
    <section className={styles.card}>
      <div className={styles.cardHead}>
        <Eyebrow>{'Readers by owner review'}</Eyebrow>
        <span className={styles.muted}>{'all time'}</span>
      </div>
      <ul className={styles.list}>
        {rows.map(r => {
          const image = safeImageUrl(r.imageUrl)
          return (
            <li key={r.noteId} className={styles.reviewRow}>
              <div className={styles.reviewHead}>
                <span className={styles.tile} aria-hidden="true">
                  {image ? (
                    // eslint-disable-next-line @next/next/no-img-element -- a 40 px DealHunt thumb, as FeedRow's
                    <img src={image} alt="" loading="lazy" />
                  ) : (
                    r.abbr
                  )}
                </span>
                <div className={styles.reviewText}>
                  {r.slug ? (
                    <Linked
                      href={threadHref(r.slug, r.noteId, trailingSlash)}
                      className={styles.reviewName}
                    >
                      {r.name}
                    </Linked>
                  ) : (
                    <span className={styles.reviewName}>{r.name}</span>
                  )}
                  <span className={styles.muted}>{r.meta}</span>
                </div>
                <div className={styles.reviewFigures}>
                  <span className={styles.reviewReaders}>{r.readers}</span>
                  {r.pace && <span className={styles.small}>{r.pace}</span>}
                </div>
              </div>
              <span className={styles.trackThin} aria-hidden="true">
                <span className={styles.fill} style={{ width: `${r.pct}%` }} />
              </span>
            </li>
          )
        })}
      </ul>
      {note && <p className={styles.note}>{note}</p>}
    </section>
  )
}

// ── Points & rank ────────────────────────────────────────────────────────────

export const PointsRankCard: React.VFC<{
  stats: Stats
  voice: Voice
  /** The summary's reply points, for the member's own rank hint. */
  replyPoints: number | null
}> = ({ stats, voice, replyPoints }) => {
  const card = pointsCard(stats)
  const hint = voice.own ? rankHint(stats, replyPoints) : null
  const parts = breakdownRows(stats)
  return (
    <section className={styles.card}>
      <Eyebrow>{'Points & rank'}</Eyebrow>
      <div className={styles.pointsHead}>
        <span className={styles.big}>
          {card.total}
          <span className={styles.unit}>{' pts'}</span>
        </span>
        {card.toNext && <span className={styles.muted}>{card.toNext}</span>}
      </div>
      <span className={styles.trackThin} aria-hidden="true">
        <span className={styles.fill} style={{ width: `${card.pct}%` }} />
      </span>
      <dl className={styles.ranks}>
        <div className={styles.rankRow}>
          <dt>{'All time'}</dt>
          <dd>{card.rankAll}</dd>
        </div>
        <div className={styles.rankRow}>
          <dt>{card.monthName}</dt>
          <dd>{card.rankMonth}</dd>
        </div>
      </dl>
      {hint && <p className={styles.hint}>{hint}</p>}
      {parts.length > 0 && (
        <div className={styles.rows}>
          <h3 className={styles.chartTitle}>
            {voice.own ? 'Where your points came from' : 'Where the points came from'}
          </h3>
          {parts.map(p => (
            <div key={p.key} className={styles.breakdownRow}>
              <span className={styles.rowLabel}>{p.label}</span>
              <span className={styles.trackThin} aria-hidden="true">
                <span className={styles.fill} style={{ width: `${p.pct}%` }} />
              </span>
              <span className={styles.rowValue}>{p.value}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

// ── Gear vs other owners ─────────────────────────────────────────────────────

export const GearVsOwnersCard: React.VFC<{
  stats: Stats
  voice: Voice
  labels: ReadonlyMap<string, ReadonlyMap<string, string>>
}> = ({ stats, voice, labels }) => {
  const rows = gearRows(stats, labels, voice)
  const followers = followersLine(stats, voice)
  const who = voice.own ? 'You' : voice.handle
  return (
    <section className={clsx(styles.card, styles.cardWide)}>
      <div className={styles.cardHead}>
        <Eyebrow>{`${possessive(voice)} gear vs other owners`}</Eyebrow>
        {followers && (
          <span className={styles.followers}>
            <strong>{followers.figure}</strong> {followers.text}
          </span>
        )}
      </div>
      {rows.map(g => (
        <div key={g.noteId} className={styles.gearRow}>
          <div className={styles.gearHead}>
            <h3 className={styles.gearName}>{g.name}</h3>
            <span className={styles.muted}>{g.others}</span>
          </div>
          <div className={styles.gearGrid}>
            <div className={styles.gearCell}>
              <span className={styles.muted}>{'Buy again'}</span>
              <div className={styles.buyAgain}>
                {g.buyAgain && (
                  <span className={clsx(styles.you, g.buyAgain === 'yes' && styles.youYes)}>
                    {`${who}: ${g.buyAgain}`}
                  </span>
                )}
                {g.communityPct !== null && (
                  <>
                    <span className={styles.track} aria-hidden="true">
                      <span
                        className={clsx(styles.fill, styles.fillLight)}
                        style={{ width: `${g.communityPct}%` }}
                      />
                    </span>
                    <span className={styles.nowrap}>{`${g.communityPct}% of owners say yes`}</span>
                  </>
                )}
              </div>
            </div>
            {g.chips.length > 0 && (
              <div className={styles.gearCell}>
                <span className={styles.muted}>{`${possessive(voice)} reads vs expectations`}</span>
                <ul className={styles.chips}>
                  {g.chips.map(c => (
                    <li key={c.id} className={clsx(styles.chip, styles[`chip_${c.tone}`])}>
                      {c.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      ))}
    </section>
  )
}

// ── Month by month ───────────────────────────────────────────────────────────

export const MonthLedger: React.VFC<{
  stats: Stats
  voice: Voice
  joinedAt: string | null
}> = ({ stats, voice, joinedAt }) => (
  <section className={clsx(styles.card, styles.cardWide, styles.ledger)}>
    <Eyebrow>{'Month by month'}</Eyebrow>
    <ul className={styles.list}>
      {ledgerRows(stats, voice, joinedAt).map(r => (
        <li key={r.key} className={clsx(styles.ledgerRow, r.quiet && styles.ledgerQuiet)}>
          <div className={styles.ledgerText}>
            <span className={styles.ledgerMonth}>{r.month}</span>
            <span className={styles.muted}>{r.what}</span>
          </div>
          <div className={styles.ledgerFigures}>
            <span className={clsx(styles.ledgerReaders, r.notRecorded && styles.faint)}>
              {r.readers}
            </span>
            <span className={styles.small}>{r.pts}</span>
          </div>
        </li>
      ))}
    </ul>
    <p className={styles.footnote}>{LEDGER_FOOTNOTE}</p>
  </section>
)
